/**
 * Path Caching Utilities
 * Manages sessionStorage caching for route paths
 */

import { PATHS_STORAGE_KEY, PATHS_VERSION_KEY } from './constants';
import { notifySessionChange } from '../session-storage-sync';

/**
 * Gets the cached paths from sessionStorage
 * @returns {Object|null} Cached paths or null if not found
 */
export function getCachedPaths() {
  if (globalThis.window === undefined) return null;
  
  try {
    const cached = sessionStorage.getItem(PATHS_STORAGE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error reading cached paths:', error);
    return null;
  }
}

/**
 * Saves paths to sessionStorage
 * @param {Object} paths - Paths object to cache
 * @param {string} version - Version identifier
 */
export function setCachedPaths(paths, version) {
  if (globalThis.window === undefined) return;
  
  try {
    sessionStorage.setItem(PATHS_STORAGE_KEY, JSON.stringify(paths));
    sessionStorage.setItem(PATHS_VERSION_KEY, version);
    // Notify other tabs — ONLY the path-cache keys, never auth tokens. A path
    // rebuild is a frequent, non-auth event; broadcasting the whole session
    // (incl. tokens) could clobber a sibling's freshly-rotated pair.
    notifySessionChange([PATHS_STORAGE_KEY, PATHS_VERSION_KEY]);
  } catch (error) {
    console.error('Error caching paths:', error);
  }
}

/**
 * Gets the cached version identifier
 * @returns {string|null}
 */
export function getCachedVersion() {
  if (globalThis.window === undefined) return null;
  
  try {
    return sessionStorage.getItem(PATHS_VERSION_KEY);
  } catch (error) {
    console.error('Error reading cached version:', error);
    return null;
  }
}

/**
 * Generates a version identifier from features tree
 * @param {Array} featuresTree - The features tree from the API
 * @returns {string} Version identifier
 */
export function generateVersion(featuresTree) {
  try {
    // Hash the serialized tree by CONTENT, not length. A length-only version
    // collides on any equal-length change — reordering nodes, or swapping a
    // handle/icon for a same-length string — so the nav kept rendering stale
    // cached paths and newly-added features never appeared. djb2 over the JSON.
    const json = JSON.stringify(featuresTree);
    let hash = 5381;
    for (let i = 0; i < json.length; i++) {
      hash = ((hash << 5) + hash + json.charCodeAt(i)) | 0;
    }
    // Bump the suffix when the path-building logic changes, to force a rebuild.
    return `${hash >>> 0}_v3`;
  } catch {
    return Date.now().toString();
  }
}

/**
 * Clears the cached paths
 */
export function clearPathsCache() {
  if (globalThis.window === undefined) return;
  
  try {
    sessionStorage.removeItem(PATHS_STORAGE_KEY);
    sessionStorage.removeItem(PATHS_VERSION_KEY);
  } catch (error) {
    console.error('Error clearing paths cache:', error);
  }
}
